import DeleteSweepRounded from '@mui/icons-material/DeleteSweepRounded';
import PersonAddAlt1Rounded from '@mui/icons-material/PersonAddAlt1Rounded';
import PointOfSaleRounded from '@mui/icons-material/PointOfSaleRounded';
import QrCodeScannerRounded from '@mui/icons-material/QrCodeScannerRounded';
import StorefrontRounded from '@mui/icons-material/StorefrontRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { api, unwrap } from '../../../api/client';
import { useAuth } from '../../../auth/AuthProvider';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import { Money } from '../../../components/Money';
import { ContactPicker, type Contact } from '../../../components/pickers';
import { errorMessage, useNotify } from '../../../lib/notify';
import { money } from '../../../lib/format';
import { useShop } from '../../../shop/ShopProvider';
import { DiscountInput } from '../components/DiscountInput';
import { QuickCustomerDialog } from '../../../components/QuickCustomerDialog';
import { useAccountBalance } from '../components/CustomerAccount';
import {
  afterSaleKeys,
  idem,
  productQuery,
  usePaymentMethods,
  usePricing,
  useTaxRates,
  type CounterSale,
  type VariantSummary,
} from '../lib/api';
import {
  cartItemFromVariant,
  cartNeedsDiscount,
  cartProblems,
  cartReducer,
  cartTotals,
  itemCount,
  parseSavedCart,
  toCounterSaleRequest,
  type CartItem,
} from '../lib/cart';
import { ComboPicker, type ComboGroupLike } from '../components/ComboPicker';
import { dec } from '../lib/decimal';
import { receiptData, invoiceDoc, shopHeader } from '../print/builders';
import { A4Document, Receipt80 } from '../print/documents';
import { usePrint } from '../print/usePrint';
import { Kbd, SumRow } from './bits';
import { CartLines } from './CartLines';
import { LineEditDialog } from './LineEditDialog';
import { ProductSearch, type ProductSearchHandle } from './ProductSearch';
import { SaleCompleteDialog } from './SaleCompleteDialog';
import { TenderDialog, type TenderInput } from './TenderDialog';
import { useShopLogo } from '../../../lib/images';

function storageKey(tenantId: string | undefined) {
  return `sm.pos.cart.${tenantId ?? 'anon'}`;
}

function readCart(key: string) {
  try {
    return parseSavedCart(localStorage.getItem(key));
  } catch {
    return parseSavedCart(null);
  }
}

/** The fast-sale counter: scan, adjust, take payment, print — on one screen, keyboard first. */
export function CounterSalePage() {
  const { me, can } = useAuth();
  const { settings, format } = useShop();
  const logo = useShopLogo();
  const notify = useNotify();
  const qc = useQueryClient();
  const pricing = usePricing();
  const methods = usePaymentMethods();
  const taxRates = useTaxRates();
  const { print, element: printElement } = usePrint();
  const key = storageKey(me?.tenant.id);

  const [cart, dispatch] = useReducer(cartReducer, key, readCart);
  const [customer, setCustomer] = useState<Contact | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [highlight, setHighlight] = useState<string | null>(null);
  const [tenderOpen, setTenderOpen] = useState(false);
  const [tenderError, setTenderError] = useState<string | null>(null);
  const [newCustomer, setNewCustomer] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [done, setDone] = useState<{
    sale: CounterSale;
    tendered: string;
    walkIn: boolean;
  } | null>(null);
  const search = useRef<ProductSearchHandle>(null);
  const attempt = useRef<{ revision: number; id: string; key: string } | null>(null);

  // Never lose a half-rung sale: the cart survives reloads on this device.
  useEffect(() => {
    try {
      if (cart.lines.length || cart.discount)
        localStorage.setItem(key, JSON.stringify({ lines: cart.lines, discount: cart.discount }));
      else localStorage.removeItem(key);
    } catch {
      /* storage unavailable: the cart just isn't kept */
    }
  }, [cart, key]);

  const totals = useMemo(() => cartTotals(cart, pricing), [cart, pricing]);
  const problems = cartProblems(cart, totals);
  const mayDiscount = can('sales.discount');
  const discountBlocked = !mayDiscount && cartNeedsDiscount(cart);
  const maySell = can('sales.deliver', 'sales.invoice');
  const empty = cart.lines.length === 0;
  const canCharge = maySell && !empty && problems.length === 0 && !discountBlocked;

  const flash = useCallback((lineKey: string) => {
    setHighlight(lineKey);
    window.setTimeout(() => setHighlight((h) => (h === lineKey ? null : h)), 900);
  }, []);

  /** Highlights the line for this variant + unit (new or bumped) for a moment. */
  const [combo, setCombo] = useState<{
    item: CartItem;
    groups: ComboGroupLike[];
    qty: string;
  } | null>(null);
  const addVariant = useCallback(
    async (v: VariantSummary, qty: string) => {
      const product = await qc.query(productQuery(v.productId)).catch(() => null);
      const item = cartItemFromVariant(v, product);
      // A combo opens its choices first; it goes on the sale with them.
      if (v.productType === 'COMBO') {
        setCombo({ item, groups: product?.combo?.groups ?? [], qty });
        return;
      }
      dispatch({ type: 'add', item, qty });
      flash(`${item.variantId}:${item.unitId}`);
      search.current?.focus();
    },
    [qc, flash],
  );

  const sell = useMutation({
    mutationFn: (input: TenderInput) => {
      if (attempt.current?.revision !== cart.revision)
        attempt.current = {
          revision: cart.revision,
          id: crypto.randomUUID(),
          key: crypto.randomUUID(),
        };
      const body = toCounterSaleRequest(cart, {
        id: attempt.current.id,
        contactId: customer && !customer.isWalkIn ? customer.id : null,
        payment: {
          amountReceived: input.amountReceived,
          methodId: input.methodId,
          reference: input.reference,
        },
      });
      return unwrap(
        api.POST('/counter-sales', { params: { header: idem(attempt.current.key) }, body }),
      );
    },
    onMutate: () => setTenderError(null),
    onSuccess: async (sale, input) => {
      attempt.current = null;
      setTenderOpen(false);
      setDone({ sale, tendered: input.amountReceived, walkIn: !customer || customer.isWalkIn });
      dispatch({ type: 'clear' });
      setCustomer(null);
      await Promise.all(afterSaleKeys.map((k) => qc.invalidateQueries({ queryKey: [...k] })));
    },
    onError: (e) => setTenderError(errorMessage(e)),
  });

  const openTender = useCallback(() => {
    if (!canCharge) return;
    setTenderError(null);
    setTenderOpen(true);
  }, [canCharge]);

  const nextSale = useCallback(() => {
    setDone(null);
    window.setTimeout(() => search.current?.focus(), 50);
  }, []);

  // Keyboard: F2 search, F9 / Ctrl+Enter pay; stray keystrokes (a scanner) go to the search box.
  const anyDialog = tenderOpen || !!editing || !!done || newCustomer || confirmClear || !!combo;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (anyDialog) return;
      if (e.key === 'F2') {
        e.preventDefault();
        search.current?.focus();
      } else if (e.key === 'F9' || ((e.ctrlKey || e.metaKey) && e.key === 'Enter')) {
        e.preventDefault();
        openTender();
      } else if (
        e.key.length === 1 &&
        !e.ctrlKey &&
        !e.metaKey &&
        !e.altKey &&
        !(e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement)
      ) {
        e.preventDefault();
        search.current?.type(e.key);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [anyDialog, openTender]);

  const shop = shopHeader(settings, undefined, logo);
  const balance = useAccountBalance(done && !done.walkIn ? done.sale.invoice.contact.id : null);
  const printReceipt = () => {
    if (!done) return;
    print(
      '80mm',
      <Receipt80
        shop={shop}
        data={receiptData(done.sale, format, {
          cashier: me?.user.name,
          tendered: done.tendered,
          walkIn: done.walkIn,
          balance,
        })}
      />,
    );
  };
  const printA4 = () => {
    if (!done) return;
    print(
      'a4',
      <A4Document
        shop={shop}
        doc={invoiceDoc(done.sale.invoice, format, !!settings?.taxEnabled)}
      />,
    );
  };

  const editLine = cart.lines.find((l) => l.key === editing) ?? null;
  const walkIn = !customer || customer.isWalkIn;
  const receivable = customer && !walkIn ? dec(customer.balances.receivable.signed) : 0n;
  const count = itemCount(cart);

  return (
    <Box>
      <Stack
        direction="row"
        sx={{ alignItems: 'center', justifyContent: 'space-between', mb: { xs: 2, sm: 3 }, gap: 2 }}
      >
        <Box>
          <Typography variant="h3">Fast sale</Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5, display: { xs: 'none', sm: 'block' } }}>
            Scan or search items, then take payment. <Kbd>F2</Kbd> search · <Kbd>F9</Kbd> pay
          </Typography>
        </Box>
        <Tooltip title="Clear sale">
          <span>
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<DeleteSweepRounded />}
              disabled={empty && !cart.discount}
              onClick={() => setConfirmClear(true)}
              sx={{ borderColor: 'divider' }}
            >
              Clear
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {!maySell && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You can build a sale here, but completing it needs the “Deliver goods” and “Post invoices”
          permissions.
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'minmax(0,1fr)',
            md: 'minmax(0, 1fr) 360px',
            lg: 'minmax(0, 1fr) 392px',
          },
          gap: { xs: 2, md: 3 },
          alignItems: 'start',
        }}
      >
        <Stack spacing={2} sx={{ minWidth: 0 }}>
          <ProductSearch
            ref={search}
            onPick={(v, q) => void addVariant(v, q)}
            onNotFound={() => undefined}
          />
          <Card sx={{ overflow: 'visible' }}>
            <Stack
              direction="row"
              sx={{
                px: { xs: 2, sm: 2.5 },
                py: 1.5,
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Typography variant="h5">Items</Typography>
              {!empty && (
                <Chip
                  size="small"
                  label={`${count} ${count === '1' ? 'item' : 'items'}`}
                  sx={{ fontWeight: 650 }}
                />
              )}
            </Stack>
            {empty ? (
              <Box sx={{ textAlign: 'center', py: { xs: 6, sm: 9 }, px: 3 }}>
                <Box
                  sx={(t) => {
                    const p = (t.vars ?? t).palette.primary.main;
                    return {
                      width: 76,
                      height: 76,
                      mx: 'auto',
                      mb: 2.5,
                      borderRadius: '22px',
                      display: 'grid',
                      placeItems: 'center',
                      color: 'primary.main',
                      background: `linear-gradient(135deg, color-mix(in srgb, ${p} 16%, transparent), color-mix(in srgb, ${p} 4%, transparent))`,
                      border: `1px solid color-mix(in srgb, ${p} 18%, transparent)`,
                    };
                  }}
                >
                  <QrCodeScannerRounded sx={{ fontSize: 36 }} />
                </Box>
                <Typography variant="h5">Ready for the next customer</Typography>
                <Typography color="text.secondary" sx={{ mt: 1, maxWidth: 380, mx: 'auto' }}>
                  Scan a barcode or type a name or SKU above. Type <b>3*</b> before a code to add
                  three at once.
                </Typography>
              </Box>
            ) : (
              <CartLines
                lines={cart.lines}
                totals={totals.lines}
                highlight={highlight}
                dispatch={dispatch}
                onEdit={setEditing}
              />
            )}
          </Card>
        </Stack>

        <Card
          sx={{
            position: { md: 'sticky' },
            top: { md: 88 },
            overflow: 'hidden',
          }}
        >
          <Box sx={{ p: 2.5, borderBottom: 1, borderColor: 'divider' }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <ContactPicker
                  type="customer"
                  value={customer}
                  onChange={setCustomer}
                  label="Customer"
                  helperText={walkIn ? 'Walk-in customer — pays in full' : undefined}
                  allowCreate
                />
              </Box>
              {can('customers.create') && (
                <Tooltip title="New customer">
                  <IconButton
                    aria-label="New customer"
                    onClick={() => setNewCustomer(true)}
                    sx={{ mt: 0.5, border: 1, borderColor: 'divider', borderRadius: '12px' }}
                  >
                    <PersonAddAlt1Rounded />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
            {!walkIn && (
              <Stack
                direction="row"
                spacing={1}
                sx={{ mt: 1.5, alignItems: 'center', justifyContent: 'space-between' }}
              >
                <Typography variant="body2" color="text.secondary">
                  {receivable > 0n ? 'Owes the shop' : receivable < 0n ? 'Has credit' : 'Balance'}
                </Typography>
                <Typography
                  variant="body2"
                  className="tabular"
                  sx={{ fontWeight: 650, color: receivable > 0n ? 'warning.main' : 'text.primary' }}
                >
                  <Money value={customer.balances.receivable.signed.replace(/^-/, '')} />
                </Typography>
              </Stack>
            )}
          </Box>

          <Box sx={{ px: 2.5, py: 2 }}>
            <SumRow label="Subtotal" value={<Money value={totals.subtotal} />} muted />
            <Box sx={{ py: 1 }}>
              <DiscountInput
                size="small"
                label="Discount on sale"
                value={cart.discount}
                disabled={!mayDiscount || empty}
                onChange={(discount) => dispatch({ type: 'discount', discount })}
                error={totals.errors.length > 0 && !!cart.discount}
                helperText={!mayDiscount ? 'Needs the “Give discounts” permission' : undefined}
              />
            </Box>
            {dec(totals.discountTotal) > 0n && (
              <SumRow
                label="Discounts"
                value={
                  <Box component="span" sx={{ color: 'success.main' }}>
                    −<Money value={totals.discountTotal} />
                  </Box>
                }
                muted
              />
            )}
            {pricing.taxEnabled && (taxRates.data?.length ?? 0) > 0 && (
              <TextField
                select
                size="small"
                fullWidth
                label="Tax on all items"
                value={
                  cart.lines.length &&
                  cart.lines.every((l) => l.taxRateId === cart.lines[0]?.taxRateId)
                    ? (cart.lines[0]?.taxRateId ?? '')
                    : '__mixed'
                }
                disabled={empty}
                onChange={(e) => {
                  const t = taxRates.data?.find((r) => r.id === e.target.value);
                  dispatch({ type: 'taxAll', tax: t ? { id: t.id, rate: t.rate } : null });
                }}
                sx={{ my: 1 }}
              >
                <MenuItem value="">No tax</MenuItem>
                <MenuItem value="__mixed" disabled sx={{ display: 'none' }}>
                  Mixed
                </MenuItem>
                {taxRates.data?.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
            {pricing.taxEnabled && dec(totals.taxTotal) > 0n && (
              <SumRow
                label={pricing.pricesIncludeTax ? 'Tax (included)' : 'Tax'}
                value={<Money value={totals.taxTotal} />}
                muted
              />
            )}
            <Divider sx={{ my: 1.5 }} />
            <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
              <Typography variant="h5">Total</Typography>
              <Typography
                variant="h2"
                className="tabular"
                data-testid="pos-total"
                sx={{ fontSize: { xs: '1.75rem', sm: '2rem' } }}
              >
                <Money value={totals.total} />
              </Typography>
            </Stack>

            {!empty && (problems.length > 0 || discountBlocked) && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {discountBlocked
                  ? 'A price below the list price or a discount needs the “Give discounts” permission.'
                  : problems[0]}
              </Alert>
            )}

            <Button
              fullWidth
              variant="contained"
              size="large"
              disabled={!canCharge}
              onClick={openTender}
              startIcon={<PointOfSaleRounded />}
              endIcon={<Kbd inverse>F9</Kbd>}
              sx={(t) => {
                const p = (t.vars ?? t).palette.primary.main;
                return {
                  mt: 2.5,
                  minHeight: 56,
                  fontSize: '1.0625rem',
                  borderRadius: '14px',
                  boxShadow: canCharge
                    ? `0 12px 24px -10px color-mix(in srgb, ${p} 70%, transparent)`
                    : undefined,
                };
              }}
            >
              Charge
              {!empty && (
                <>
                  &nbsp;
                  <Money value={totals.total} />
                </>
              )}
            </Button>
            <Stack
              direction="row"
              spacing={0.75}
              sx={{
                mt: 1.5,
                justifyContent: 'center',
                alignItems: 'center',
                color: 'text.secondary',
              }}
            >
              <StorefrontRounded sx={{ fontSize: 16 }} />
              <Typography variant="caption">
                Stock leaves the default location when the sale completes
              </Typography>
            </Stack>
          </Box>
        </Card>
      </Box>

      {combo && (
        <ComboPicker
          name={combo.item.name}
          price={combo.item.price}
          groups={combo.groups}
          format={format}
          onClose={() => setCombo(null)}
          onPick={(picks, price) => {
            dispatch({ type: 'add', item: { ...combo.item, price, combo: picks }, qty: combo.qty });
            flash(`${combo.item.variantId}:${combo.item.unitId}`);
            setCombo(null);
            search.current?.focus();
          }}
        />
      )}
      {editLine && (
        <LineEditDialog
          key={editLine.key}
          line={editLine}
          taxRates={taxRates.data ?? []}
          pricing={pricing}
          onClose={() => setEditing(null)}
          onRemove={() => {
            dispatch({ type: 'remove', key: editLine.key });
            setEditing(null);
          }}
          onSave={(patch) => {
            dispatch({ type: 'update', key: editLine.key, patch });
            setEditing(null);
          }}
        />
      )}

      {tenderOpen && (
        <TenderDialog
          open
          total={totals.total}
          customer={customer}
          methods={methods.data ?? []}
          busy={sell.isPending}
          error={tenderError}
          onCancel={() => setTenderOpen(false)}
          onComplete={(input) => sell.mutate(input)}
        />
      )}

      <SaleCompleteDialog
        sale={done?.sale ?? null}
        balance={balance}
        onPrintReceipt={printReceipt}
        onPrintA4={printA4}
        onNext={nextSale}
        whatsapp={
          done && {
            documentType: 'sales_invoice',
            documentId: done.sale.invoice.id,
            documentRef: done.sale.invoice.number,
            contact: { ...done.sale.invoice.contact, isWalkIn: done.walkIn },
            vars: {
              number: done.sale.invoice.number,
              name: done.walkIn ? null : done.sale.invoice.contact.name,
              amount: money(done.sale.invoice.total, format),
            },
            pdf: {
              fileName: `${done.sale.invoice.number}.pdf`,
              paper: '80mm',
              render: () => (
                <Receipt80
                  shop={shop}
                  data={receiptData(done.sale, format, {
                    cashier: me?.user.name,
                    tendered: done.tendered,
                    walkIn: done.walkIn,
                    balance,
                  })}
                />
              ),
            },
          }
        }
      />

      <QuickCustomerDialog
        open={newCustomer}
        onClose={() => setNewCustomer(false)}
        onCreated={(c) => {
          setCustomer(c);
          setNewCustomer(false);
        }}
      />

      <ConfirmDialog
        open={confirmClear}
        title="Clear this sale?"
        body="All items and the discount are removed. Nothing has been charged yet."
        confirmLabel="Clear sale"
        danger
        onClose={() => setConfirmClear(false)}
        onConfirm={() => {
          dispatch({ type: 'clear' });
          setCustomer(null);
          notify.info('Sale cleared');
          window.setTimeout(() => search.current?.focus(), 50);
          return Promise.resolve();
        }}
      />
      {printElement}
    </Box>
  );
}
